import { zodResolver } from '@hookform/resolvers/zod';
import type { HeadersFunction } from '@remix-run/node';
import { Link, useNavigate, type MetaFunction } from '@remix-run/react';
import { UserAdd } from 'iconsax-reactjs';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PhoneInput from '~/components/form/PhoneInput';
import { useCustomer } from '~/context/CustomerContext';
import { useFlashMessages } from '~/context/FlashMessagesContext';
import { pushSignUp, pushUserData, createUserData } from '~/gtm';
import { useRoutePath } from '~/hooks/useRoutePath';
import Default from '~/layouts/Default';
import { registerSchema, type RegisterFormValues } from '~/schemas/register.schema';
import { apiFetch } from '~/utils/apiFetch';
import { privatePageHeaders } from '~/utils/cacheControl.server';
import { buildPageMeta } from '~/utils/meta';
import { useAccountVerificationRequired } from '~/utils/useChannelConfig';

interface ApiViolation {
  propertyPath: string;
  message: string;
}

interface ApiErrorShape {
  violations?: ApiViolation[];
  message?: string;
  ['hydra:description']?: string;
}

export const meta: MetaFunction = ({ matches }) => buildPageMeta('Registrace', matches);

export const headers: HeadersFunction = ({ parentHeaders }) => privatePageHeaders(parentHeaders);

export default function RegisterPage() {
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { addMessage } = useFlashMessages();
  const { setCustomerUrl, refetchCustomer } = useCustomer();
  const accountVerificationRequired = useAccountVerificationRequired();
  const routePath = useRoutePath();
  const { t } = useTranslation();

  // Log the freshly registered customer in using the credentials they just submitted.
  // Only used when the channel does not require e-mail verification (double opt-in off),
  // where the account is active immediately. Mirrors the login page flow: the token
  // endpoint sets the JWT cookie and returns the customer IRI, which CustomerContext
  // needs in localStorage before it can load the customer.
  async function autoLogin(email: string, password: string): Promise<boolean> {
    try {
      const API_URL = window.ENV?.API_URL;
      if (!API_URL) return false;

      const response = await apiFetch(
        `${API_URL}/api/v2/shop/customers/token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        },
        { skipAuthRedirect: true }
      );

      if (!response.ok) return false;

      const data: { customer?: string } = await response.json();
      if (!data.customer) return false;

      setCustomerUrl(data.customer);
      await refetchCustomer();
      return true;
    } catch {
      return false;
    }
  }

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      subscribeNewsletter: false,
    },
  });

  async function onSubmit(formData: RegisterFormValues) {
    setLoading(true);

    try {
      const API_URL = window.ENV?.API_URL;
      if (!API_URL) throw new Error('API URL not configured.');

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber?.trim() || null,
        password: formData.password,
        subscribedToNewsletter: formData.subscribeNewsletter,
      };

      const response = await apiFetch(`${API_URL}/api/v2/shop/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const text = await response.text();

      let data: unknown = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        console.log('Failed to parse response JSON:', text);
      }

      const isApiError = (val: unknown): val is ApiErrorShape => typeof val === 'object' && val !== null;

      if (!response.ok) {
        if (isApiError(data) && Array.isArray(data.violations)) {
          data.violations.forEach((v: ApiViolation) => {
            const fieldName = v.propertyPath as keyof RegisterFormValues;
            if (fieldName in registerSchema.shape || fieldName === 'confirmPassword') {
              setError(fieldName, { message: v.message });
            }
          });
          addMessage('error', t('pages.register.messages.registration_failed'));
        } else {
          const fallback = isApiError(data) && (data['hydra:description'] || data.message);
          addMessage('error', fallback || t('pages.register.messages.registration_error'));
        }
        return;
      }

      // GTM: Push sign_up event
      pushSignUp('email');

      // GTM: Push user_data for Enhanced Conversions
      // Extract user_id from response @id (format: /api/v2/shop/customers/123)
      const customerData = data as { '@id'?: string } | null;
      const userId = customerData?.['@id']?.split('/').pop();
      const userData = createUserData({
        userId,
        email: formData.email,
        phone: formData.phoneNumber || undefined,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      pushUserData(userData, 'registered', 'new');

      // With double opt-in enabled the account stays disabled until the customer clicks the
      // verification link, so we cannot log them in — send them to the thank-you page that
      // tells them to check their e-mail.
      if (accountVerificationRequired) {
        addMessage('success', t('pages.register.messages.registration_success'));
        navigate(routePath('registerThankYou'), { replace: true });
        return;
      }

      // Double opt-in disabled → the account is active right away. Log the customer in and
      // take them to their dashboard instead of pointing them at a verification e-mail that
      // will never be sent. If auto-login fails for any reason, fall back to the login page.
      const loggedIn = await autoLogin(formData.email, formData.password);
      if (loggedIn) {
        addMessage('success', t('pages.register.messages.registration_success_logged_in'));
        navigate(routePath('accountDashboard'), { replace: true });
      } else {
        addMessage('success', t('pages.register.messages.registration_success_login'));
        navigate(routePath('login'), { replace: true });
      }
    } catch (err: unknown) {
      addMessage('error', err instanceof Error ? err.message : t('pages.register.messages.unexpected_error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Default>
      <div className="container my-auto">
        <div className="row justify-content-center py-5">
          <div className="col-12 col-md-8 col-lg-6">
            <h1 className="h2 mb-1">{t('pages.register.title')}</h1>
            <p className="mb-4">
              {t('pages.register.have_account')}{' '}
              <Link to={routePath('login')} className="link-reset" rel="nofollow">
                {t('pages.register.sign_in_here')}
              </Link>
            </p>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <h2 className="h5 mb-3">{t('pages.register.personal_info')}</h2>

              {/* First Name */}
              <div className="mb-3">
                <label htmlFor="firstName" className="form-label ">
                  {t('pages.register.first_name')}
                </label>
                <input
                  id="firstName"
                  type="text"
                  required
                  className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                  autoComplete="given-name"
                  {...register('firstName')}
                />
                {errors.firstName && <div className="invalid-feedback">{t(errors.firstName.message as string)}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="lastName" className="form-label">
                  {t('pages.register.last_name')}
                </label>
                <input
                  id="lastName"
                  type="text"
                  required
                  className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                  autoComplete="family-name"
                  {...register('lastName')}
                />
                {errors.lastName && <div className="invalid-feedback">{t(errors.lastName.message as string)}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="email" className="form-label ">
                  {t('pages.register.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  autoComplete="email"
                  {...register('email')}
                />
                {errors.email && <div className="invalid-feedback">{t(errors.email.message as string)}</div>}
              </div>

              <div className="mb-3">
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      id="phoneNumber"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      label={t('pages.register.phone')}
                      error={errors.phoneNumber ? t(errors.phoneNumber.message as string) : undefined}
                      required
                    />
                  )}
                />
              </div>

              <div className="form-check mb-5">
                <input
                  id="newsletter"
                  type="checkbox"
                  className="form-check-input"
                  {...register('subscribeNewsletter')}
                />
                <label htmlFor="newsletter" className="form-check-label">
                  {t('pages.register.newsletter')}
                </label>
              </div>

              <h2 className="h5 mb-3">{t('pages.register.credentials')}</h2>

              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  {t('pages.register.password')}
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  autoComplete="new-password"
                  {...register('password')}
                />
                {errors.password && <div className="invalid-feedback">{t(errors.password.message as string)}</div>}
              </div>

              <div className="mb-4">
                <label htmlFor="confirmPassword" className="form-label ">
                  {t('pages.register.confirm_password')}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  autoComplete="new-password"
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && (
                  <div className="invalid-feedback">{t(errors.confirmPassword.message as string)}</div>
                )}
              </div>

              <div className="d-grid mb-5">
                <Button type="submit" disabled={loading}>
                  <UserAdd size="20" aria-hidden="true" /> {t('pages.register.submit')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Default>
  );
}
